import { TextField, Typography, MenuItem } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import OutlinedSelect from '../../controls/OutlinedSelect';
import mappings from './Mappings';
import { isEmpty } from 'lodash';

const useStyles = makeStyles((theme) => ({
    whiteBackground: {
        backgroundColor: 'white',
    },
    gridItem: {
        marginTop: theme.spacing(2),
    },
}));

function FieldMappingSection({ field, updateField, lastSetMapping }) {
    const classes = useStyles();
    const { mappingsUsed, parentMapping } = useSelector(({ fieldDialog }) => fieldDialog);
    const { multipleFieldIndex } = useSelector(({ formBuilder }) => formBuilder);
    const [udoMappingGroup, setUdoMappingGroup] = useState('');
    const [udoMappingField, setUdoMappingField] = useState('');
    const [targetPath, setTargetPath] = useState('');

    // Any time field prop changes, reset targetPath, udoMappingGroup, and udoMappingField to new values
    useEffect(() => {
        const fullTargetPath = field?.fieldMapping ? field.fieldMapping.targetPath : '';
        if (fullTargetPath) {
            let udoMappingComponents;
            let udoGroup;
            let udoField;
            let newTargetPath;
            if (fullTargetPath.includes('customData')) {
                if (field.typeId === 'multiple') {
                    udoMappingComponents = field.fieldMapping.targetPath.replaceAll("']", '').split("['");
                    udoGroup = udoMappingComponents[1];
                    newTargetPath = udoMappingComponents[0];
                } else {
                    udoMappingComponents = field.fieldMapping.targetPath.replaceAll("']", '').split("['");
                    udoGroup = udoMappingComponents[1];
                    udoField = udoMappingComponents?.length ? udoMappingComponents[2] : '';
                    newTargetPath = udoMappingComponents[0];
                }
            } else {
                newTargetPath = fullTargetPath;
            }
            newTargetPath && setTargetPath(newTargetPath);
            udoGroup && setUdoMappingGroup(udoGroup);
            udoField && setUdoMappingField(udoField);
        }
    }, [field]);

    const updateUdoGroup = (event) => {
        setUdoMappingGroup(event.target.value);
    };

    const updateUdoField = (event) => {
        setUdoMappingField(event.target.value);
    };

    const updateTargetPath = (event) => {
        setTargetPath(event.target.value);
    };

    // If field is mapped to a UDO field, concatenate the pieces of the
    // target path before storing the full value back in field object
    const updateMapping = () => {
        let pathValue;
        if (targetPath?.includes('customData')) {
            if (field.typeId === 'multiple') {
                pathValue = `${targetPath}['${udoMappingGroup}']`;
            } else {
                pathValue = `${targetPath}['${udoMappingGroup}']['${udoMappingField}']`;
            }
            updateField({
                target: {
                    id: 'fieldMapping',
                    value: { targetPath: pathValue },
                },
            });
        } else if (targetPath || (targetPath === '' && !isEmpty(field))) {
            updateField({
                target: {
                    id: 'fieldMapping',
                    value: { targetPath: appendPathPrefix(targetPath) },
                },
            });
        }
    };
    const memoizedUpdate = useCallback(updateMapping, [targetPath, udoMappingGroup, udoMappingField]);

    const appendPathPrefix = (path) => {
        //Add a '$.' to the start of the targetPath of nested fields if none is present.
        return path.startsWith('$.') || path === '' ? path : '$.' + path;
    };

    const stripPathPrefix = (path) => {
        // Remove path prefix for display in sub-fields within a repeatable field
        return path.replaceAll('$.', '');
    };

    useEffect(() => {
        if (targetPath || targetPath === '') {
            memoizedUpdate();
        }
    }, [targetPath, memoizedUpdate]);

    const isMappingUsed = (mapping) => {
        return mappingsUsed.includes(mapping);
    };

    const mappingsList = [
        <MenuItem key="none" value="">
            <em>None</em>
        </MenuItem>,
        ...mappings
            .filter(
                (mapping) =>
                    (mapping.fieldType === 'multiple' && field.typeId === 'multiple') ||
                    mapping.parentFieldMapping === parentMapping ||
                    (mapping.fieldType !== 'multiple' &&
                        !mapping.parentFieldMapping &&
                        multipleFieldIndex < 0 &&
                        field.typeId !== 'multiple'),
            )
            .map((choice) => (
                <MenuItem
                    key={choice.label}
                    value={choice.targetPath}
                    disabled={
                        (!lastSetMapping || lastSetMapping.targetPath !== choice.targetPath) &&
                        isMappingUsed(choice.targetPath)
                    }
                >
                    {choice.label}
                </MenuItem>
            )),
    ];

    return (
        <React.Fragment>
            {field.typeId !== 'textBlock' && field.typeId !== 'fileUpload' && (
                <Grid className={classes.gridItem} item>
                    {parentMapping.includes('customData') ? (
                        <>
                            <Typography gutterBottom variant="subtitle2" className={classes.grey}>
                                Field Mapping
                            </Typography>
                            <TextField
                                id="targetPath"
                                variant="outlined"
                                fullWidth
                                className={classes.whiteBackground}
                                value={stripPathPrefix(targetPath)}
                                onChange={updateTargetPath}
                                placeholder="UDO Field"
                            />
                        </>
                    ) : (
                        <OutlinedSelect
                            id={'targetPath'}
                            name={'targetPath'}
                            label="Field Mapping"
                            className={classes.whiteBackground}
                            value={targetPath}
                            onChange={updateTargetPath}
                        >
                            {mappingsList}
                        </OutlinedSelect>
                    )}
                </Grid>
            )}
            {field?.fieldMapping?.targetPath?.includes('customData') && (
                <Grid container spacing={2} style={{ padding: '20px 0px' }}>
                    <Grid item xs={field.typeId === 'multiple' ? 12 : 6}>
                        <TextField
                            id="udoGroup"
                            multiline
                            variant="outlined"
                            label="Group"
                            fullWidth
                            className={classes.whiteBackground}
                            onChange={updateUdoGroup}
                            value={udoMappingGroup}
                        />
                    </Grid>
                    {field.typeId !== 'multiple' && (
                        <Grid item xs={6}>
                            <TextField
                                id="udoField"
                                multiline
                                variant="outlined"
                                label="Field"
                                fullWidth
                                className={classes.whiteBackground}
                                onChange={updateUdoField}
                                value={udoMappingField}
                            />
                        </Grid>
                    )}
                </Grid>
            )}
        </React.Fragment>
    );
}

export default FieldMappingSection;
